import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { downloadPdf } from "@/lib/files";
import { fmtDA } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { G50SaisieDialog } from "@/components/G50SaisieDialog";
import { G50Ecarts } from "@/components/G50Ecarts";
import { G50Card } from "@/components/dashboard/G50Card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { downloadExcel } from "@/lib/files";
import { FileDown, FileSpreadsheet, Pencil, Loader2 } from "lucide-react";

const COLS = [["ca_ht", "CA HT"], ["tva_collectee", "TVA collectée"], ["tva_deductible", "TVA déductible"], ["precompte", "Précompte antérieur"],
  ["tva_a_payer", "TVA à payer"], ["credit_reporter", "Crédit à reporter"], ["timbre", "Timbre"], ["saisies", "IRG / IBS / autres"], ["total_a_payer", "Total à payer"]];
const saisies = (r) => r.irg_salaires + r.ibs_acompte + r.taxe_apprentissage + r.tap + r.autres;
const SEL = "h-9 rounded-md border bg-background px-2 text-sm";

export default function EtablirG50() {
  const { societes, societeId } = useApp();
  const { role } = useAuth();
  const editable = role === "administrateur" || role === "comptabilite";
  const [sid, setSid] = useState("");
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const soc = societes.find((s) => s.id === sid);
  const choices = societes;

  useEffect(() => {
    if (societeId !== "all") setSid(societeId);
    else if (!sid && choices.length) setSid(choices[0].id);
  }, [societeId, societes]); // eslint-disable-line

  const load = () => {
    if (!sid) return;
    setRows(null);
    api.get(`/g50-decl/${sid}/${annee}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(() => { load(); }, [sid, annee]); // eslint-disable-line

  const pdf = (m) => downloadPdf(`/g50-decl/${sid}/${annee}/${m}/pdf`, `G50-${(soc?.name || "").replace(/\s+/g, "-")}-${annee}-${String(m).padStart(2, "0")}`);

  return (
    <div data-testid="etablir-g50-page">
      <PageHeader title="Écart G50" subtitle="Déclaration mensuelle calculée automatiquement à partir des factures de vente et d'achat" />
      <Tabs defaultValue="mensuel">
        <TabsList className="mb-4">
          <TabsTrigger value="mensuel" data-testid="g50-tab-mensuel">G50 mensuels</TabsTrigger>
          <TabsTrigger value="etat" data-testid="g50-tab-etat">État TVA &amp; timbre</TabsTrigger>
          <TabsTrigger value="ecarts" data-testid="g50-tab-ecarts">Écarts factures / G50</TabsTrigger>
        </TabsList>
      <div className="rounded-xl border bg-card p-5">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <select className={SEL} value={sid} onChange={(e) => setSid(e.target.value)} data-testid="g50d-societe">
            {choices.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select className={SEL} value={annee} onChange={(e) => setAnnee(Number(e.target.value))} data-testid="g50d-annee">
            {[2025, 2026].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <p className="text-xs text-muted-foreground">Le crédit de TVA est reporté automatiquement sur le mois suivant.</p>
          <Button variant="outline" size="sm" className="gap-2 ml-auto" onClick={() => downloadExcel(`/g50-decl/${sid}/${annee}/excel`, `G50-${(soc?.name || "").replace(/\s+/g, "-")}-${annee}`)} data-testid="g50d-excel">
            <FileSpreadsheet className="h-4 w-4" /> Excel {annee}
          </Button>
        </div>
        <TabsContent value="etat"><G50Card societeId={sid} exercice={annee} /></TabsContent>
        <TabsContent value="ecarts">{sid && <G50Ecarts sid={sid} socName={soc?.name} editable={editable} />}</TabsContent>
        <TabsContent value="mensuel">
        {rows === null ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1200px]">
              <thead><tr className="text-left text-muted-foreground border-b">
                <th className="py-2 pr-2 font-medium">Mois</th>
                {COLS.map(([k, h]) => <th key={k} className="py-2 pr-2 font-medium text-right">{h}</th>)}
                <th className="py-2 font-medium text-right">Actions</th>
              </tr></thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.mois} data-testid={`g50d-row-${r.mois}`} className={r.nb_ventes || r.nb_achats ? "" : "text-muted-foreground"}>
                    <td className="py-2 pr-2 font-medium">{r.label}</td>
                    {COLS.map(([k]) => (
                      <td key={k} className={`py-2 pr-2 tabular text-right ${k === "total_a_payer" ? "font-bold" : ""} ${k === "credit_reporter" && r[k] ? "text-emerald-600" : ""}`}>
                        {fmtDA(k === "saisies" ? saisies(r) : r[k])}
                      </td>
                    ))}
                    <td className="py-2 text-right whitespace-nowrap">
                      {editable && <Button size="sm" variant="ghost" onClick={() => setEdit(r)} data-testid={`g50d-edit-${r.mois}`}><Pencil className="h-4 w-4" /></Button>}
                      <Button size="sm" variant="outline" className="gap-1" onClick={() => pdf(r.mois)} data-testid={`g50d-pdf-${r.mois}`}><FileDown className="h-4 w-4" /> PDF</Button>
                    </td>
                  </tr>
                ))}
                <tr className="font-bold border-t-2 border-primary/30" data-testid="g50d-total">
                  <td className="py-2">TOTAL</td>
                  {COLS.map(([k]) => <td key={k} className="py-2 pr-2 tabular text-right">{["precompte", "credit_reporter"].includes(k) ? "" : fmtDA(rows.reduce((a, r) => a + (k === "saisies" ? saisies(r) : r[k]), 0))}</td>)}
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
        </TabsContent>
      </div>
      </Tabs>
      {edit && <G50SaisieDialog row={edit} societeId={sid} annee={annee} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
